import React, { useState, useEffect } from 'react';
import { useGlobalToast } from './modern_toast';
import api from '../services/api';

function ModernCartSidebar({ isOpen, onClose, cartItems = [], onUpdateCart }) {
  const [items, setItems] = useState(cartItems);
  const [updatingItems, setUpdatingItems] = useState({});
  const { addToast } = useGlobalToast();

  useEffect(() => {
    setItems(cartItems);
  }, [cartItems]);

  const updateQuantity = async (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeItem(itemId);
      return;
    }

    // Marcar item como "atualizando"
    setUpdatingItems(prev => ({ ...prev, [itemId]: true }));

    try {
      // ============================================================
      // SE USUÁRIO LOGADO: SINCRONIZAR COM BACKEND PRIMEIRO
      // ============================================================
      if (sessionStorage.getItem('login') && !sessionStorage.getItem('login').includes('/')) {
        const usuario = sessionStorage.getItem('login');
        const loja = sessionStorage.getItem('loja');

        console.log('🔄 Atualizando quantidade no banco:', { itemId, newQuantity });

        // Enviar atualização para o backend
        await api.post('add_session_db', {
          dict_itens: {
            usuario: usuario,  // Email/token do usuário (como sistema antigo)
            loja: loja,
            comprar: itemId,
            quantidade: newQuantity
          }
        });

        console.log('✅ Quantidade atualizada no banco com sucesso');
      }

      // ============================================================
      // ATUALIZAR ESTADO LOCAL APÓS CONFIRMAÇÃO DO BACKEND
      // ============================================================
      const updatedItems = items.map(item => {
        if (item.id === itemId) {
          return { ...item, quantidade: newQuantity };
        }
        return item;
      });

      setItems(updatedItems);
      if (onUpdateCart) {
        onUpdateCart(updatedItems);
      }

    } catch (error) {
      console.error('❌ Erro ao atualizar quantidade:', error);
      addToast('Erro ao atualizar quantidade. Tente novamente.', 'error');
    } finally {
      // Remover estado de "atualizando"
      setUpdatingItems(prev => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
    }
  };

  const removeItem = async (itemId) => {
    // Marcar item como "atualizando"
    setUpdatingItems(prev => ({ ...prev, [itemId]: true }));

    try {
      // ============================================================
      // SE USUÁRIO LOGADO: SINCRONIZAR COM BACKEND PRIMEIRO
      // Backend remove automaticamente itens com quantidade = 0
      // ============================================================
      if (sessionStorage.getItem('login') && !sessionStorage.getItem('login').includes('/')) {
        const usuario = sessionStorage.getItem('login');
        const loja = sessionStorage.getItem('loja');

        console.log('🗑️ Removendo item do banco:', itemId);

        // Enviar quantidade = 0 para remover do backend
        await api.post('add_session_db', {
          dict_itens: {
            usuario: usuario,  // Email/token do usuário (como sistema antigo)
            loja: loja,
            comprar: itemId,
            quantidade: 0  // 0 = remover
          }
        });

        console.log('✅ Item removido do banco com sucesso');
      }

      // ============================================================
      // REMOVER DO ESTADO LOCAL APÓS CONFIRMAÇÃO DO BACKEND
      // ============================================================
      const updatedItems = items.filter(item => item.id !== itemId);
      setItems(updatedItems);
      if (onUpdateCart) {
        onUpdateCart(updatedItems);
      }

      addToast('Produto removido do carrinho', 'success');

    } catch (error) {
      console.error('❌ Erro ao remover item:', error);
      addToast('Erro ao remover produto. Tente novamente.', 'error');
    } finally {
      // Remover estado de "atualizando"
      setUpdatingItems(prev => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
    }
  };

  const calculateTotal = () => {
    return items.reduce((total, item) => {
      const preco = item.preco || 0;
      const quantidade = item.quantidade || 1;
      return total + (preco * quantidade);
    }, 0);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const goToCheckout = () => {
    if (sessionStorage['login']) {
      window.location.href = '/checkout';
    } else {
      window.location.href = '/login';
    }
  };

  return (
    <>
      {/* Offcanvas no padrão do filtro */}
      <div
        className={`offcanvas offcanvas-end modern-filter-offcanvas ${isOpen ? 'show' : ''}`}
        tabIndex="-1"
        style={{
          visibility: isOpen ? 'visible' : 'hidden',
          width: '85%',
          maxWidth: '400px'
        }}
      >
        {/* Header offcanvas */}
        <div className="offcanvas-header modern-filter-offcanvas-header">
          <h5 className="offcanvas-title">
            <i className="bi bi-bag me-2"></i>
            Carrinho
            {items.length > 0 && (
              <span className="badge bg-primary ms-2">{items.length}</span>
            )}
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
            aria-label="Fechar carrinho"
          ></button>
        </div>

        {/* Body offcanvas */}
        <div className="offcanvas-body modern-filter-offcanvas-body" style={{ overflowY: 'auto' }}>
          {items.length === 0 ? (
            <div className="text-center py-5 px-3">
              <i className="bi bi-bag-x fs-1 text-muted mb-3"></i>
              <h6>Carrinho vazio</h6>
              <p className="text-muted">Adicione produtos para começar suas compras</p>
              <button
                className="btn btn-modern w-100 text-white"
                onClick={onClose}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Continuar comprando
              </button>
            </div>
          ) : (
            <>
              {/* Lista de itens */}
              <div className="cart-items p-2">
                {items.map((item, index) => (
                  <div key={index} className="cart-item">
                    <div className="cart-item-image">
                      <img 
                        src={item.img || '/placeholder-image.jpg'} 
                        alt={item.nome || 'Produto'}
                        onError={(e) => {
                          e.target.src = '/placeholder-image.jpg';
                        }}
                      />
                    </div>
                    
                    <div className="cart-item-details">
                      <h6 className="cart-item-title">
                        {item.nome || 'Produto sem nome'}
                      </h6>
                      <p className="cart-item-price mb-2">
                        {formatPrice(item.preco || 0)}
                      </p>
                      
                      {/* Controles de quantidade */}
                      <div className="quantity-controls">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => updateQuantity(item.id, (item.quantidade || 1) - 1)}
                          disabled={(item.quantidade || 1) <= 1 || updatingItems[item.id]}
                        >
                          {updatingItems[item.id] ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          ) : (
                            <i className="bi bi-dash"></i>
                          )}
                        </button>
                        <span className="quantity-display">
                          {updatingItems[item.id] ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          ) : (
                            item.quantidade || 1
                          )}
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => updateQuantity(item.id, (item.quantidade || 1) + 1)}
                          disabled={updatingItems[item.id]}
                        >
                          {updatingItems[item.id] ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          ) : (
                            <i className="bi bi-plus"></i>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="cart-item-actions">
                      <button
                        className="btn btn-link p-0 text-danger"
                        onClick={() => removeItem(item.id)}
                        aria-label="Remover item"
                        disabled={updatingItems[item.id]}
                      >
                        {updatingItems[item.id] ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : (
                          <i className="bi bi-trash"></i>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer offcanvas */}
        {items.length > 0 && (
          <div className="offcanvas-footer modern-filter-offcanvas-footer">
            <div className="w-100">
              <div className="cart-total mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold">Total:</span>
                  <span className="fs-5 fw-bold text-primary">
                    {formatPrice(calculateTotal())}
                  </span>
                </div>
              </div>

              <div className="d-flex gap-2 cart-buttons-row">
                <button
                  className="btn btn-outline-modern flex-fill"
                  onClick={onClose}
                >
                  Continuar Comprando
                </button>
                <button
                  className="btn btn-modern text-white flex-fill"
                  onClick={goToCheckout}
                >
                  <i className="bi bi-credit-card me-2"></i>
                  Finalizar Compra
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="offcanvas-backdrop fade show"
          onClick={onClose}
        ></div>
      )}
    </>
  );
}

export default ModernCartSidebar;
